import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { BrandedPayButton } from '@/components/nfl/BrandedPayButton';
import { useLocanceGeoCheck } from '@/hooks/useLocanceGeoCheck';
import { usePaypalEligibility } from '@/hooks/usePaypalEligibility';
import { useNativeAppVersion } from '@/hooks/useNativeAppVersion';
import { GEO_ENABLED, GEO_OBSERVE } from '@/lib/locance';
import { mapDepositError } from '@/lib/depositErrors';
import { GeoBlockScreen } from '@/components/locance/GeoBlockScreen';
import { pingBeforeMoneyActionIfStale } from '@/lib/devicePing';
import WalletShell from '@/components/wallet/WalletShell';
import { DepositFeeDisclosure } from '@/components/wallet/DepositFeeDisclosure';
import { SoapCheckoutFrame } from '@/components/wallet/SoapCheckoutFrame';
import { launchPersonaInquiry, recheckKycGate } from '@/lib/personaKyc';
import { cleanBankLabel } from '@/lib/walletActivity';
import { setPendingJoin, getPendingJoin, clearPendingJoin } from '@/lib/pendingJoin';
import {
  ChevronLeft, ChevronRight, Landmark, CreditCard, Plus, Lock, Check, ShieldCheck, Settings, Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { RAIL_PAYPAL, RAIL_SOAP } from '@/lib/depositRails';
import { feeSentence } from '@/lib/depositFees';

// SAB-565 pt 1: account-level deposit redesign. Source of truth: the design handoff
// README at design-handoff/deposit-flow/README.md. Token mapping (README -> real):
//   --bg=bg-background, --bg-elev-1=bg-card, --bg-elev-2=bg-popover, --bg-muted=bg-muted,
//   --fg-muted=text-muted-foreground, --fg-subtle=text-muted-foreground/60,
//   --sf-orange=primary, --gold/--success/--damage=mapped colors, --shadow-sabotage=CSS var,
//   .sf-eyebrow=inline uppercase tracking utility.
// Money is integer CENTS everywhere; formatted only at the display edge.

const MIN_DEPOSIT_CENTS = 1000; // $10 — now enforced server-side too (soap-create-checkout)
const MAX_DEPOSIT_CENTS = 1_000_000; // $10,000 sanity ceiling (matches money paths)
const CHIPS: number[] = [2500, 10000, 25000, 50000];

// SAB item 1 (certifier): Soap card deposit is gated PER-ACCOUNT, not by a global flag.
// The authoritative gate is server-side in soap-create-checkout; the client reads its
// own membership via soap_access_enabled() (fail-closed) only to show/hide the option.

type Method = 'bank' | 'card' | 'paypal' | 'venmo' | null;
type Phase = 'form' | 'handoff' | 'checkout' | 'processing' | 'success' | 'error';

const fmt = (cents: number) =>
  (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtWhole = (cents: number) =>
  Number.isInteger(cents / 100) ? String(cents / 100) : fmt(cents);

const eyebrow = 'text-[11px] uppercase tracking-[0.08em] text-muted-foreground font-medium';

interface LimitStatus {
  period: 'daily' | 'weekly' | 'monthly';
  limitCents: number;
  usedCents: number;
}
const PERIOD_WORD: Record<string, string> = { daily: 'today', weekly: 'this week', monthly: 'this month' };

// Match the Tailwind `lg` breakpoint (1024px) on viewport WIDTH, so the summary +
// CTA renders in exactly one rail at a time (see the render below). We intentionally
// do NOT reuse useIsMobile here: that hook keys on touch + smaller-dimension, which
// disagrees with the CSS `lg:` width breakpoint on a narrow non-touch window.
// Initialized synchronously from matchMedia so there is no first-paint flash.
function useIsLgUp() {
  const query = '(min-width: 1024px)';
  const [isLgUp, setIsLgUp] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setIsLgUp(mq.matches);
    mq.addEventListener('change', onChange);
    onChange();
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isLgUp;
}

function WalletDepositInner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [params] = useSearchParams();
  const isLgUp = useIsLgUp();
  // Per-source PayPal/Venmo eligibility (region/device/merchant config). Drives
  // hiding an ineligible method row instead of rendering a dead button.
  const funding = usePaypalEligibility();
  // Build 6 Phase A: PayPal/Venmo rows show on web OR native 1.2.0+ (never native
  // below 1.2.0). Venmo additionally still requires funding.venmo eligibility.
  const { atLeast } = useNativeAppVersion();
  const canUseRestoredPath = !Capacitor.isNativePlatform() || atLeast('1.2.0');

  const [loading, setLoading] = useState(true);
  const [leagueId, setLeagueId] = useState<string | null>(null);
  const [zeroLeague, setZeroLeague] = useState(false);
  const [balanceCents, setBalanceCents] = useState(0);
  const [linkedBank, setLinkedBank] = useState<{ id: string; name: string; last4: string } | null>(null);
  const [limits, setLimits] = useState<LimitStatus | null>(null);

  const [amountStr, setAmountStr] = useState(''); // raw dollar string the user types
  const [selectedChip, setSelectedChip] = useState<number | null>(null);
  const [method, setMethod] = useState<Method>(null);
  const [phase, setPhase] = useState<Phase>('form');
  const [submitting, setSubmitting] = useState(false);
  const [newBalanceCents, setNewBalanceCents] = useState<number | null>(null);
  // SAB-622: the Soap hosted checkout returns via a FULL page reload (?soap_return=1), so
  // amountStr — and the amountCents memo derived from it — reset to empty/0. The success
  // screen must show what was actually deposited, so we carry the amount from the pending
  // row (which the return poll already reads) rather than the now-empty input.
  const [successAmountCents, setSuccessAmountCents] = useState<number | null>(null);
  // SAB-657: the embedded Soap checkout URL (rendered in an in-app iframe) and a bump key
  // that (re)starts the pending-row poll — from either the iframe completion signal or the
  // legacy full-reload return. One poll implementation, two triggers.
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [depositPollKey, setDepositPollKey] = useState(0);

  // SAB-566 Fix 3: KYC. kyc_status is DISPLAY-ONLY (drives the proactive banner);
  // the server gate is authoritative and released via recheckKycGate.
  const [kycStatus, setKycStatus] = useState<string | null>(null);
  const [kycVerifying, setKycVerifying] = useState(false);
  // SAB item 1: per-account Soap availability (UX gate only; the server enforces).
  // Fail-closed: false until soap_access_enabled() explicitly returns true.
  const [soapEnabled, setSoapEnabled] = useState(false);
  useEffect(() => {
    if (!user) { setSoapEnabled(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await (supabase as any).rpc('soap_access_enabled');
        if (!cancelled) setSoapEnabled(!error && data === true);
      } catch {
        if (!cancelled) setSoapEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);
  const kycApproved = kycStatus === 'approved';
  // Re-run the exact submit (with CURRENT state) after Persona approves, when
  // verification was triggered by a blocked deposit attempt (avoids stale closures).
  const pendingSubmitRef = useRef(false);
  const submitRef = useRef<() => void>(() => {});

  const amountCents = useMemo(() => {
    const n = parseFloat(amountStr);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.round(n * 100);
  }, [amountStr]);

  // ── Load account context ────────────────────────────────────────────────
  const loadContext = useCallback(async () => {
    if (!user) return;
    // SAB-595: when funding a paid join, the TARGET league is passed explicitly —
    // a not-yet-member has no membership to derive it from (this is why the flow
    // did not work before). Existing users depositing into their own league keep
    // the membership-derived path unchanged.
    const explicitLeague = params.get('league');
    let lg: string | null;
    if (explicitLeague) {
      lg = explicitLeague;
    } else {
      const { data: mem } = await supabase
        .from('nfl_league_members')
        .select('league_id, joined_at')
        .eq('user_id', user.id)
        .order('joined_at', { ascending: false })
        .limit(1);
      lg = (mem && mem[0]?.league_id) || null;
    }
    setLeagueId(lg);
    setZeroLeague(!lg);

    if (lg) {
      const { data: w } = await supabase
        .from('user_wallets')
        .select('balance')
        .eq('user_id', user.id)
        .maybeSingle();
      setBalanceCents(Math.round(Number((w as any)?.balance ?? 0) * 100));
    }

    const { data: banks } = await supabase
      .from('linked_bank_accounts')
      .select('id, bank_name, account_last_four, status')
      .eq('user_id', user.id)
      .eq('status', 'active');
    const b = (banks || [])[0] as any;
    setLinkedBank(b ? { id: b.id, name: cleanBankLabel(b.bank_name), last4: b.account_last_four || '' } : null);

    const { data: lim } = await supabase.rpc('get_deposit_limit_status', { p_user_id: user.id });
    if (lim) {
      const period = (lim as any).tightest_period as 'daily' | 'weekly' | 'monthly';
      const p = (lim as any)[period];
      setLimits({ period, limitCents: p.limit_cents, usedCents: p.used_cents });
    }

    // SAB-566 Fix 3: kyc_status drives the proactive verify banner (display only).
    const { data: prof } = await supabase.from('profiles').select('kyc_status').eq('id', user.id).maybeSingle();
    setKycStatus((prof as any)?.kyc_status ?? null);

    setLoading(false);
  }, [user, params]);

  useEffect(() => { loadContext(); }, [loadContext]);

  // SAB-595: "Add funds and join" arrives with ?league=&amount=&join=&shortfall=.
  // Prefill the amount and persist the pending join (1h TTL) so the return path
  // survives the PayPal external-browser round trip. Runs once on mount.
  useEffect(() => {
    const amt = params.get('amount');
    if (amt && !amountStr) setAmountStr(amt);
    const join = params.get('join');
    const lg = params.get('league');
    if (join && lg) setPendingJoin(join, lg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SAB-595: a successful deposit that was initiated to fund a join returns the
  // user to the join page (covers the web methods — Aeropay inline, Soap return,
  // PayPal web popup; native PayPal returns via NativeUrlHandler instead).
  useEffect(() => {
    if (phase !== 'success') return;
    const pj = getPendingJoin();
    if (pj) { clearPendingJoin(); navigate(`/nfl/join/${pj.code}`); }
  }, [phase, navigate]);

  // ── Return from the Soap checkout: processing state driven by the pending-row
  //    status (never assume success; the webhook is the truth). ──
  // Trigger A (SAB-657): the iframe completion signal calls startDepositPoll() directly.
  // Trigger B (legacy): a full-reload return (?soap_return=1) from a non-embedded flow.
  const startDepositPoll = useCallback(() => {
    setCheckoutUrl(null);
    setPhase('processing');
    setDepositPollKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (params.get('soap_return') !== '1' || !user) return;
    window.history.replaceState({}, '', window.location.pathname);
    startDepositPoll();
  }, [params, user, startDepositPoll]);

  // The single poll implementation, (re)started by bumping depositPollKey.
  useEffect(() => {
    if (depositPollKey === 0 || !user) return;
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      if (cancelled) return;
      tries++;
      const { data } = await supabase
        .from('soap_pending_deposits')
        .select('status, amount_cents')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      const st = (data as any)?.status;
      if (st === 'succeeded') {
        await loadContext();
        const { data: w } = await supabase.from('user_wallets').select('balance').eq('user_id', user.id).maybeSingle();
        setNewBalanceCents(Math.round(Number((w as any)?.balance ?? 0) * 100));
        // SAB-622: source the deposited amount from the pending row, not the (reset) input.
        const depCents = Math.round(Number((data as any)?.amount_cents ?? 0));
        if (depCents > 0) setSuccessAmountCents(depCents);
        setPhase('success');
        return;
      }
      if (['failed', 'voided', 'returned', 'expired', 'terminally_failed'].includes(st)) {
        setPhase('error');
        toast({ title: 'Deposit not completed', description: 'Your card deposit did not go through. You were not charged, or any charge is reversed.', variant: 'destructive' });
        return;
      }
      // SAB-657: bounded give-up. Soap's in-widget cancel/X is indistinguishable from a
      // completion at the client (their docs emit only soap:checkout-complete), so an
      // abandoned checkout lands here with the row still 'created'. Return to the form with
      // an HONEST message that covers both outcomes — never imply money moved. The webhook
      // stays the source of truth: a real completion credits + resolves before this fires;
      // a genuine slow-settle still credits later and updates the balance.
      if (tries >= 16) { setPhase('form'); toast({ title: "We couldn't confirm your deposit", description: "If you completed payment, your balance updates automatically once it settles. If you cancelled, you weren't charged." }); return; }
      setTimeout(poll, 1500);
    };
    poll();
    return () => { cancelled = true; };
  }, [depositPollKey, user, loadContext, toast]);

  // ── Derived (never stored) ──────────────────────────────────────────────
  const remainingCents = limits ? Math.max(limits.limitCents - limits.usedCents, 0) : Infinity;
  const belowMinimum = amountCents > 0 && amountCents < MIN_DEPOSIT_CENTS;
  // SAB-595: when funding a join and the shortfall is under the $10 minimum, we
  // prefilled $10 — explain why + the leftover so we never silently over-ask.
  const joinShortfall = Number(params.get('shortfall') || 0);
  const minBumped =
    !!params.get('join') && joinShortfall > 0 && joinShortfall < 10 && amountCents === MIN_DEPOSIT_CENTS;
  const joinLeftover = minBumped ? 10 - joinShortfall : 0;
  const overLimit = limits ? amountCents > remainingCents : false;
  const overMax = amountCents > MAX_DEPOSIT_CENTS;
  const meterPct = limits && limits.limitCents > 0 ? Math.min(100, Math.round((limits.usedCents / limits.limitCents) * 100)) : 0;

  const ctaLabel = useMemo(() => {
    if (method === 'card') return 'Continue to secure card checkout';
    if (method === 'paypal') return 'Continue with PayPal';
    if (method === 'venmo') return 'Continue with Venmo';
    // Fix 5: bind to the SAME live amount as the order summary — never a stale
    // fallback. Empty/zero → "Deposit" (no number).
    return amountCents > 0 ? `Deposit $${fmt(amountCents)}` : 'Deposit';
  }, [method, amountCents]);

  const amountValid = amountCents >= MIN_DEPOSIT_CENTS && !overLimit && !overMax;
  const ctaDisabled = !amountValid || !method || submitting;

  // ── Interactions ────────────────────────────────────────────────────────
  const pickChip = (c: number) => { setSelectedChip(c); setAmountStr(String(c / 100)); };
  const onAmountChange = (v: string) => {
    const clean = v.replace(/[^0-9.]/g, '');
    setAmountStr(clean);
    const cents = Math.round((parseFloat(clean) || 0) * 100);
    setSelectedChip(CHIPS.includes(cents) ? cents : null);
  };

  const handleUnlinkedBank = () => {
    // Existing Aerosync bank-link lives in the league-funds surface. Interim: open
    // it there with a returnTo; on return, this screen re-fetches and the linked
    // row appears. (Full return-context wiring is out of scope for this PR.)
    navigate(`/nfl?fundsLinkBank=1&returnTo=${encodeURIComponent('/wallet/deposit')}`);
  };

  const callEdge = async (name: string, body: any) => {
    const { data, error } = await supabase.functions.invoke(name, { body });
    if (error) {
      let parsed: any = null;
      const ctx: any = (error as any)?.context;
      try { if (ctx?.clone) parsed = await ctx.clone().json(); } catch { /* not json */ }
      const e = new Error(parsed?.error || error.message || 'Request failed') as Error & { code?: string; body?: any };
      if (parsed?.code) e.code = parsed.code;
      // SAB-631: carry the full parsed 403 body so mapDepositError can read the geo block
      // category + detected_state (names the state on a jurisdiction block).
      if (parsed && typeof parsed === 'object') e.body = parsed;
      throw e;
    }
    if (data?.error) throw new Error(data.error);
    return data;
  };

  // SAB-566 Fix 3: launch the tier-1 Persona flow directly (no dead toast). When
  // triggered by a blocked deposit attempt, resume that exact submit after the
  // server gate confirms — the SDK status never releases money (recheckKycGate).
  const startKyc = async (triggeredBySubmit: boolean) => {
    if (!user || kycVerifying) return;
    setKycVerifying(true);
    pendingSubmitRef.current = triggeredBySubmit;
    try {
      await launchPersonaInquiry({
        tier: 1,
        userId: user.id,
        onComplete: async () => {
          const ok = await recheckKycGate(user.id, 1);
          setKycVerifying(false);
          if (ok) {
            setKycStatus('approved');
            await loadContext();
            if (pendingSubmitRef.current) { pendingSubmitRef.current = false; submitRef.current(); }
            else toast({ title: 'Identity verified', description: 'You can now add funds.' });
          } else {
            toast({ title: 'Verification pending', description: "We're still confirming your identity — try again in a moment." });
          }
        },
        onCancel: () => setKycVerifying(false),
        onError: () => { setKycVerifying(false); toast({ title: 'Verification error', description: 'Something went wrong during verification. Try again.', variant: 'destructive' }); },
      });
    } catch {
      setKycVerifying(false);
      toast({ title: 'Verification unavailable', description: 'Could not load the verification client. Try again.', variant: 'destructive' });
    }
  };

  // SAB-570: geo gate for the account-level deposit surface (mirrors NFLFundsSection).
  // acquireGeoSession gathers a FRESH Locance session per money action to attach as
  // geo_session_id; a failed/blocked gather aborts the deposit and geo.geoBlock drives
  // <GeoBlockScreen/>. GEO off → no session (server gate off too), flow unchanged.
  const geo = useLocanceGeoCheck();
  const acquireGeoSession = async (): Promise<{ ok: boolean; sessionId?: string }> => {
    // SAB-572 DEBUG: entry + flag value (remove after the device test is conclusive).
    console.log('[SAB-572] acquireGeoSession ENTERED — GEO_ENABLED=', GEO_ENABLED, 'rawVITE_GEO_ENABLED=', import.meta.env.VITE_GEO_ENABLED);
    // SAB-488: gather when enforcing (GEO_ENABLED) OR observing (GEO_OBSERVE).
    if (!GEO_ENABLED && !GEO_OBSERVE) { console.log('[SAB-572] acquireGeoSession SHORT-CIRCUIT (geo off) — no gather'); return { ok: true }; }
    console.log('[SAB-572] acquireGeoSession — gathering (enforce or observe)…');
    const verdict = await geo.run();
    console.log('[SAB-572] acquireGeoSession — geo.run() returned result=', verdict?.result ?? 'null');
    // SAB item 5: geo.run() refreshed the cached GPS fix; device-ping if the last one is
    // stale (>30 min). Telemetry, non-blocking, never prompts.
    pingBeforeMoneyActionIfStale(leagueId);
    // OBSERVE (enforce off): send the session for server recording, never fail-close.
    if (!GEO_ENABLED) return { ok: true, sessionId: verdict?.sessionId };
    if (verdict?.result === 'Compliant') return { ok: true, sessionId: verdict.sessionId };
    return { ok: false };
  };

  // SAB-605: one router for every deposit method (PayPal via onBrandError, plus the
  // Soap/Aeropay paths below). mapDepositError turns the edge fn's code into human
  // copy + an action — so a working geo block reads as a location message, not
  // "Payment failed", and KYC routes to Persona instead of a dead retry.
  const routeMoneyError = (err: any) => {
    const { title, message, action } = mapDepositError(err);
    if (action === 'verify_identity') { startKyc(true); return; }   // launch Persona
    if (action === 'responsible_gaming') { toast({ title, description: message, variant: 'destructive' }); navigate('/responsible-gaming'); return; }
    // geo / min-deposit / insufficient / generic → surface the mapped copy
    toast({ title, description: message, variant: 'destructive' });
  };

  const submit = async () => {
    // SAB-572 DEBUG: does the deposit handler even fire, and which path? (remove after test)
    console.log('[SAB-572] WalletDeposit.submit ENTERED. method=', method, 'ctaDisabled=', ctaDisabled, 'leagueId=', leagueId, 'linkedBank=', !!linkedBank, 'soapEnabled=', soapEnabled);
    if (ctaDisabled || !leagueId) return;
    if (method === 'card') {
      if (!soapEnabled) return; // not enrolled (row is hidden; defensive)
      setSubmitting(true); setPhase('handoff');
      try {
        const geoSess = await acquireGeoSession();
        if (!geoSess.ok) { setSubmitting(false); setPhase('form'); return; }
        const res = await callEdge('soap-create-checkout', {
          amount_cents: amountCents, league_id: leagueId,
          // SAB-657: embed the checkout in-app (iframe) rather than navigating the whole
          // app out to Soap's hosted page. Same value on web + native.
          experience: 'iframe',
          return_url: `${window.location.origin}/wallet/deposit?soap_return=1`,
          ...(geoSess.sessionId ? { geo_session_id: geoSess.sessionId } : {}),
        });
        if (res?.url) { setCheckoutUrl(res.url); setPhase('checkout'); return; }
        setPhase('form'); toast({ title: 'Card checkout failed', description: 'Could not start checkout.', variant: 'destructive' });
      } catch (err) { setPhase('form'); routeMoneyError(err); } finally { setSubmitting(false); }
      return;
    }
    // SAB-767: the method === 'bank' branch is GONE with the Aeropay rail. Nothing can set
    // method to 'bank' any more — the MethodRow that did was the Aeropay bank picker — so the
    // branch was unreachable, and leaving an unreachable call to a retired money endpoint is
    // how a dead rail comes back to life by accident.
    // paypal / venmo render their own SDK button (handled below).
  };

  // Keep submitRef on the latest submit so the post-Persona resume (startKyc)
  // re-runs with the current amount/method, not a stale closure.
  useEffect(() => { submitRef.current = submit; });

  // ── Render ──────────────────────────────────────────────────────────────
  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  // SAB-657: embedded Soap checkout takes over the screen; completion (postMessage from our
  // in-frame return page) starts the poll, cancel returns to the form.
  if (phase === 'checkout' && checkoutUrl) {
    return (
      <SoapCheckoutFrame
        url={checkoutUrl}
        onComplete={startDepositPoll}
        onCancel={() => { setCheckoutUrl(null); setPhase('form'); }}
      />
    );
  }

  if (phase === 'processing') return <ProcessingState />;
  // SAB-622: prefer the amount carried from the pending row (Soap return path, where the
  // input has reset); fall back to the live input for the inline methods that keep state.
  if (phase === 'success') return <SuccessState amountCents={successAmountCents ?? amountCents} newBalanceCents={newBalanceCents ?? balanceCents} onDone={() => navigate('/wallet')} />;

  // SAB-570: a failed/blocked geo gather takes over with the friendly 4c screen
  // (the client front door). The server geo gate stays the authoritative backstop.
  if (GEO_ENABLED && geo.geoBlock) {
    return (
      <div className="min-h-[60vh] px-4 py-6">
        <GeoBlockScreen
          reason={geo.geoBlock}
          busy={geo.state === 'prompting' || geo.state === 'checking'}
          onRetry={() => submitRef.current?.()}
          onDismiss={() => geo.reset()}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="mx-auto w-full max-w-[940px] px-[22px] pt-4 pb-[30px] lg:px-8 lg:pt-10 lg:pb-14">
        {/* Back link */}
        <button onClick={() => navigate('/wallet')} className="flex items-center gap-[5px] text-[15px] font-medium text-muted-foreground mb-2 hover:text-foreground">
          <ChevronLeft className="h-5 w-5" strokeWidth={2.2} /> <span className="hidden lg:inline">Back to </span>Wallet
        </button>
        <h1 className="font-display font-extrabold text-[30px] lg:text-[34px] tracking-[-0.02em] text-foreground mb-[22px]">Deposit</h1>

        {/* SAB-566 Fix 3: proactive verify banner — verification is a step, not a
            failure (orange accent, README card language). Launches the same Persona
            tier-1 flow the KYC_REQUIRED path uses. */}
        {!zeroLeague && !kycApproved && (
          <div className="rounded-[14px] border bg-card px-4 py-4 mb-6 flex items-start gap-3" style={{ borderColor: 'hsl(25 95% 53% / 0.3)' }}>
            <span className="h-[38px] w-[38px] rounded-[10px] grid place-items-center shrink-0" style={{ background: 'hsl(25 95% 53% / 0.12)' }}>
              <ShieldCheck className="h-5 w-5 text-primary" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold text-[15px] text-foreground">Verify your identity to add funds</p>
              <p className="text-[13px] leading-[1.45] text-muted-foreground mt-0.5">A quick one-time check keeps your deposits and withdrawals secure.</p>
              <button onClick={() => startKyc(false)} disabled={kycVerifying}
                className="mt-3 inline-flex items-center gap-2 h-9 px-4 rounded-full bg-primary text-white font-display font-bold text-[13px] hover:bg-primary/90 disabled:opacity-60">
                {kycVerifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Verify identity
              </button>
            </div>
          </div>
        )}

        {zeroLeague ? (
          <ZeroLeagueGate onCreate={() => navigate('/nfl/create')} onBrowse={() => navigate('/nfl/leagues/browse')} />
        ) : (
          <div className="lg:grid lg:grid-cols-[1fr_372px] lg:gap-9 lg:items-start">
            {/* LEFT: amount + method */}
            <div>
              <p className={cn(eyebrow, 'mb-[10px]')}>Amount</p>
              <AmountInput value={amountStr} onChange={onAmountChange} invalid={overLimit || belowMinimum || overMax} />
              {belowMinimum && <p className="text-[13px] text-damage mt-2">Minimum deposit is ${fmt(MIN_DEPOSIT_CENTS)}.</p>}
              {minBumped && (
                <p className="text-[13px] text-muted-foreground mt-2">
                  Minimum deposit is ${fmt(MIN_DEPOSIT_CENTS)}. You'll have ${joinLeftover.toFixed(2)} left over after joining.
                </p>
              )}

              <div className="flex gap-[9px] mt-[14px] mb-7">
                {CHIPS.map((c) => {
                  const over = overLimit && c > remainingCents;
                  const sel = selectedChip === c;
                  return (
                    <button key={c} onClick={() => pickChip(c)}
                      className={cn('flex-1 h-[46px] rounded-[12px] font-display text-[15px] border transition-colors',
                        over ? 'bg-damage/10 border-damage/40 text-[hsl(0_84%_68%)]'
                        : sel ? 'bg-primary/[0.14] border-[1.5px] border-primary text-primary font-bold'
                        : 'bg-muted border-border text-foreground font-semibold')}>
                      ${fmtWhole(c)}
                    </button>
                  );
                })}
              </div>

              {overLimit && limits && <LimitNotice limits={limits} remainingCents={remainingCents} meterPct={meterPct} onSettings={() => navigate('/responsible-gaming')} />}

              <p className={cn(eyebrow, 'mb-3')}>Payment method</p>
              <div role="radiogroup" className="flex flex-col gap-[10px]">
                {/* Bank row: linked (selectable) OR action row. Rail-gated
                    (depositRails): Aeropay declined the application; its
                    bank-link/ACH surfaces are hidden unless re-enabled. */}
                {soapEnabled && RAIL_SOAP && (
                  <MethodRow selected={method === 'card'} onSelect={() => setMethod('card')} icon={<CreditCard className="h-5 w-5" />} label="Debit / credit card" sublabel="Instant" />
                )}
                {/* SAB (native PayPal/Venmo gate): hidden on native — the PayPal SDK's
                    window.open crashes Capacitor's WKWebView. Web keeps both. Display
                    gate only; reversed when the @capacitor/browser fix lands.
                    Additionally, each row renders only when the SDK reports that
                    funding source ELIGIBLE (usePaypalEligibility). On live Venmo is
                    ineligible, so its row is hidden rather than rendering a dead
                    button; `funding` starts not-ready with both false, so nothing
                    flashes in then disappears. */}
                {/* Rail-gated (depositRails). The `!soapEnabled` exclusion is GONE: every
                    enabled rail now renders together and the user picks. It existed so
                    Soap-enrolled accounts saw a Soap-only world; that made PayPal
                    unreachable for exactly the accounts that could transact. */}
                {canUseRestoredPath && RAIL_PAYPAL && funding.paypal && (
                  <MethodRow selected={method === 'paypal'} onSelect={() => setMethod('paypal')} icon={<span className="text-[13px] font-bold text-[#003087]">PP</span>} label="PayPal" sublabel="Instant" />
                )}
                {canUseRestoredPath && RAIL_PAYPAL && funding.venmo && (
                  <MethodRow selected={method === 'venmo'} onSelect={() => setMethod('venmo')} icon={<span className="text-[13px] font-bold text-[#3d95ce]">V</span>} label="Venmo" sublabel="Instant" />
                )}
              </div>

              {/* Per-rail fee, recomputed on every amount/rail change. Soap debit returns
                  null (no fee to pass on); PayPal and Venmo show the gross-up so the user
                  sees what they are charged BEFORE confirming, and that the round amount is
                  what lands. */}
              {(() => {
                // amountCents is the single in-scope amount and is integer cents; the fee
                // model works in dollars. Derived here rather than passing cents in, so the
                // unit is converted exactly once.
                const rail = method === 'card' ? 'card' : method === 'venmo' ? 'venmo' : 'paypal';
                const line = feeSentence(rail, amountCents / 100);
                return line ? (
                  <p className="mt-3 text-[12px] leading-snug text-muted-foreground">{line}</p>
                ) : null;
              })()}

              {/* Summary + CTA. Rendered in EXACTLY ONE rail at a time via the JS
                  breakpoint (not a CSS display toggle), so BrandedPayButton and its
                  PayPal/Venmo SDK button mount once — never as a hidden twin, which
                  is what broke the button at mobile width. Mobile: below the methods. */}
              {!isLgUp && (
                <div className="mt-[26px]">
                  <SummaryAndCta amountCents={amountCents} method={method} ctaLabel={ctaLabel} ctaDisabled={ctaDisabled} submitting={submitting}
                    onSubmit={submit} leagueId={leagueId} callEdge={callEdge} acquireGeoSession={acquireGeoSession} onBrandSuccess={async () => { await loadContext(); setPhase('success'); }} onBrandError={routeMoneyError} onBrandPending={() => setPhase('processing')} />
                </div>
              )}
            </div>

            {/* RIGHT rail (desktop, sticky). Only mounted at lg+ so the button never
                double-mounts across breakpoints (see the mobile block above). */}
            {isLgUp && (
              <div className="sticky top-6 rounded-[16px] border border-border p-[26px]" style={{ background: 'var(--gradient-card)' }}>
                <p className={cn(eyebrow, 'mb-3')}>Order summary</p>
                <SummaryAndCta amountCents={amountCents} method={method} ctaLabel={ctaLabel} ctaDisabled={ctaDisabled} submitting={submitting}
                  onSubmit={submit} leagueId={leagueId} callEdge={callEdge} acquireGeoSession={acquireGeoSession} onBrandSuccess={async () => { await loadContext(); setPhase('success'); }} onBrandError={routeMoneyError} onBrandPending={() => setPhase('processing')} />
              </div>
            )}
          </div>
        )}
      </div>

      {phase === 'handoff' && <HandoffSheet />}
    </div>
  );
}

/* ── Sub-components ──────────────────────────────────────────────────────── */

function AmountInput({ value, onChange, invalid }: { value: string; onChange: (v: string) => void; invalid: boolean }) {
  return (
    <div className={cn('h-[74px] rounded-[16px] flex items-center px-[22px] border',
      invalid ? 'bg-damage/[0.07] border-[1.5px] border-damage/60' : 'bg-muted border-border')}>
      <span className={cn('font-display font-semibold text-[30px]', invalid ? 'text-[hsl(0_84%_68%)]' : 'text-muted-foreground')}>$</span>
      <input inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} placeholder="0"
        className="flex-1 bg-transparent outline-none font-display font-bold text-[32px] tracking-[-0.02em] text-foreground tabular-nums pl-1 caret-primary" />
    </div>
  );
}

function MethodRow({ selected, onSelect, icon, label, sublabel }: { selected: boolean; onSelect: () => void; icon: React.ReactNode; label: string; sublabel?: string }) {
  return (
    <button role="radio" aria-checked={selected} onClick={onSelect}
      className={cn('flex items-center gap-[14px] rounded-[14px] px-4 py-[15px] border text-left transition-all',
        selected ? 'bg-primary/[0.06] border-[1.5px] border-primary' : 'bg-card border-border hover:border-primary/30')}
      style={selected ? { boxShadow: '0 0 0 3px hsl(25 95% 53% / 0.1)' } : undefined}>
      <span className="h-10 w-10 rounded-[11px] grid place-items-center text-foreground" style={{ background: 'hsl(222 30% 16%)' }}>{icon}</span>
      <span className="flex-1">
        <span className="block text-[15.5px] font-semibold text-foreground leading-tight">{label}</span>
        {sublabel && <span className="block text-[13px] text-muted-foreground mt-px">{sublabel}</span>}
      </span>
      <span className={cn('h-[22px] w-[22px] rounded-full border-2 grid place-items-center', selected ? 'border-primary' : 'border-border')}>
        {selected && <span className="h-[11px] w-[11px] rounded-full bg-primary" />}
      </span>
    </button>
  );
}

function SummaryAndCta({ amountCents, method, ctaLabel, ctaDisabled, submitting, onSubmit, leagueId, callEdge, acquireGeoSession, onBrandSuccess, onBrandError, onBrandPending }: {
  amountCents: number; method: Method; ctaLabel: string; ctaDisabled: boolean; submitting: boolean; onSubmit: () => void;
  leagueId: string; callEdge: (n: string, b: any) => Promise<any>;
  acquireGeoSession: () => Promise<{ ok: boolean; sessionId?: string }>;
  onBrandSuccess: () => void; onBrandError: (err: unknown) => void; onBrandPending: () => void;
}) {
  // Summary as a rows array so a fee row slots in later without a redesign.
  const rows: { label: string; value: string; strong?: boolean }[] = [
    { label: 'Deposit', value: `$${fmt(amountCents)}` },
  ];
  return (
    <div>
      <div className="rounded-[16px] border border-border bg-card px-[18px] py-4 mb-[18px]">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between">
            <span className="text-[15px] text-muted-foreground">{r.label}</span>
            <span className="font-display font-semibold text-[16px] text-foreground tabular-nums">{r.value}</span>
          </div>
        ))}
        <div className="border-t border-border my-3" />
        <div className="flex items-center justify-between">
          <span className="text-[15px] font-semibold text-foreground">To your wallet</span>
          <span className="font-display font-extrabold text-[20px] text-foreground tabular-nums">${fmt(amountCents)}</span>
        </div>
        {/* SAB-582: fee disclosure. Renders null under DEPOSIT_FEE_MODE=absorb (today),
            so the deposit screen is unchanged until the disclosed gross_up cutover. */}
        <DepositFeeDisclosure
          amountCents={amountCents}
          processor={method === 'bank' ? 'aeropay' : method === 'card' ? 'soap' : 'paypal'}
        />
      </div>

      {(method === 'paypal' || method === 'venmo') && amountCents > 0 ? (
        // key={method} forces a fresh mount when the user switches PayPal<->Venmo, so
        // the SDK button is torn down and rebuilt with the correct funding source
        // instead of reusing the first one (Venmo previously opened PayPal checkout).
        <BrandedPayButton key={method} fundingSource={method} amount={amountCents / 100} leagueId={leagueId} callEdgeFunction={callEdge} acquireGeoSession={acquireGeoSession} onSuccess={onBrandSuccess} onError={onBrandError} onPending={onBrandPending} />
      ) : (
        <>
          <button disabled={ctaDisabled} onClick={onSubmit}
            className={cn('w-full h-14 rounded-full font-display font-bold text-[17px] flex items-center justify-center gap-[9px] transition-colors',
              ctaDisabled ? 'text-muted-foreground/60 cursor-not-allowed' : 'bg-primary text-white hover:bg-primary/90')}
            style={ctaDisabled ? { background: 'hsl(222 30% 15%)' } : { boxShadow: 'var(--shadow-sabotage)' }}>
            {submitting ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <Lock className="h-[18px] w-[18px]" />}
            {ctaLabel}
          </button>
          {!method && <p className="text-[13px] text-muted-foreground/60 text-center mt-2">Choose a payment method</p>}
        </>
      )}

      <p className="text-[12px] leading-[1.5] text-muted-foreground/60 text-center mt-4 px-1.5">
        Must be 18+ (19+ in Nebraska) and physically located in an eligible state. Deposits are final.
      </p>
    </div>
  );
}

function LimitNotice({ limits, remainingCents, meterPct, onSettings }: { limits: LimitStatus; remainingCents: number; meterPct: number; onSettings: () => void }) {
  const word = PERIOD_WORD[limits.period];
  return (
    <div className="rounded-[14px] border bg-card px-4 pt-4 pb-[14px] mb-7" style={{ borderColor: 'hsl(45 93% 47% / 0.3)' }}>
      <div className="flex items-start gap-3">
        <span className="h-[34px] w-[34px] rounded-[10px] grid place-items-center shrink-0" style={{ background: 'hsl(45 93% 47% / 0.14)' }}>
          <ShieldCheck className="h-[19px] w-[19px] text-gold" />
        </span>
        <div className="flex-1">
          <p className="font-display font-bold text-[15px] text-foreground">You can deposit up to ${fmtWhole(remainingCents)} more {word}</p>
          <p className="text-[13px] leading-[1.45] text-muted-foreground mt-0.5">That keeps you under your {limits.period} deposit limit. Lower the amount to continue.</p>
        </div>
      </div>
      <div className="h-2 rounded-full bg-muted mt-3 overflow-hidden"><div className="h-full rounded-full bg-gold" style={{ width: `${meterPct}%` }} /></div>
      <div className="flex items-center justify-between text-[12px] mt-1.5">
        <span className="text-muted-foreground">${fmtWhole(limits.usedCents)} of ${fmtWhole(limits.limitCents)} used {word}</span>
        <span className="text-gold font-semibold">${fmtWhole(remainingCents)} left</span>
      </div>
      <div className="border-t border-border my-4 mb-3" />
      <button onClick={onSettings} className="flex items-center gap-2.5 w-full text-left">
        <Settings className="h-[17px] w-[17px] text-muted-foreground shrink-0" />
        <span className="flex-1">
          <span className="block text-[14px] font-semibold text-primary">Adjust deposit limits in Responsible Gaming settings</span>
          <span className="block text-[12px] text-muted-foreground/60">Increases take effect after 24 hours.</span>
        </span>
        <ChevronRight className="h-4 w-4 text-muted-foreground/60" />
      </button>
    </div>
  );
}

function HandoffSheet() {
  return (
    <div className="fixed inset-0 z-50 flex items-end" style={{ background: 'rgba(4,6,12,0.6)' }}>
      <div className="w-full rounded-t-[26px] border-t border-border px-[26px] pt-[14px] pb-[34px] text-center bg-popover">
        <div className="mx-auto w-10 h-[5px] rounded-full mb-[26px]" style={{ background: 'rgba(255,255,255,0.2)' }} />
        <div className="mx-auto h-[66px] w-[66px] rounded-[20px] grid place-items-center mb-5" style={{ background: 'hsl(25 95% 53% / 0.12)', border: '1px solid hsl(25 95% 53% / 0.3)' }}>
          <Lock className="h-[30px] w-[30px] text-primary" />
        </div>
        <h2 className="font-display font-bold text-[19px] text-foreground">Taking you to secure checkout…</h2>
        <p className="text-[14px] leading-[1.5] text-muted-foreground mt-2">You'll be redirected to enter your card. We never see your card number.</p>
        <div className="mx-auto mt-5 h-7 w-7 rounded-full animate-spin" style={{ border: '3px solid hsl(25 95% 53% / 0.25)', borderTopColor: 'hsl(25 95% 53%)' }} />
        <p className="flex items-center justify-center gap-1.5 text-[12.5px] text-muted-foreground/60 mt-5"><Lock className="h-[13px] w-[13px]" /> Powered by <span className="text-muted-foreground">Soap Payments</span></p>
      </div>
    </div>
  );
}

function ProcessingState() {
  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center px-8 text-center">
      <div className="h-14 w-14 rounded-full animate-spin mb-6" style={{ border: '3px solid hsl(25 95% 53% / 0.2)', borderTopColor: 'hsl(25 95% 53%)' }} />
      <h2 className="font-display font-bold text-[21px] text-foreground">Processing your deposit</h2>
      <p className="text-[14px] leading-[1.55] text-muted-foreground mt-2 max-w-[250px]">Your balance updates automatically once the payment is confirmed, usually a few seconds.</p>
      <div className="mt-6 h-[5px] w-[220px] rounded-full bg-muted overflow-hidden">
        <div className="h-full w-2/5 rounded-full bg-primary" style={{ animation: 'sf-indeterminate 1.4s ease-in-out infinite' }} />
      </div>
      <style>{`@keyframes sf-indeterminate{0%{transform:translateX(-100%)}100%{transform:translateX(320%)}}`}</style>
    </div>
  );
}

function SuccessState({ amountCents, newBalanceCents, onDone }: { amountCents: number; newBalanceCents: number; onDone: () => void }) {
  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center px-8 text-center">
      <div className="h-[88px] w-[88px] rounded-full grid place-items-center mb-[26px]" style={{ background: 'hsl(160 84% 39% / 0.14)', border: '1px solid hsl(160 84% 39% / 0.35)', animation: 'sf-pop 0.5s cubic-bezier(0.34,1.56,0.64,1)' }}>
        <Check className="h-11 w-11 text-success" strokeWidth={2.4} />
      </div>
      <h2 className="font-display font-extrabold text-[26px] text-foreground">${fmt(amountCents)} added</h2>
      <p className="text-[15px] text-muted-foreground">to your wallet</p>
      <div className="mt-6 w-full max-w-[320px] rounded-[14px] border border-border bg-card px-[18px] py-[15px] flex items-center justify-between">
        <span className="text-[14px] text-muted-foreground">New balance</span>
        <span className="font-display font-extrabold text-[20px] text-success tabular-nums">${fmt(newBalanceCents)}</span>
      </div>
      <button onClick={onDone} className="mt-6 h-[52px] w-full max-w-[320px] rounded-full bg-primary text-white font-display font-bold">Done</button>
      <style>{`@keyframes sf-pop{0%{transform:scale(0.3)}60%{transform:scale(1.15)}100%{transform:scale(1)}}`}</style>
    </div>
  );
}

function ZeroLeagueGate({ onCreate, onBrowse }: { onCreate: () => void; onBrowse: () => void }) {
  return (
    <div className="rounded-[16px] border border-border bg-card p-6 text-center">
      <p className="text-[15px] text-foreground font-semibold">Join or create a league to add funds</p>
      <p className="text-[13px] text-muted-foreground mt-1.5 leading-relaxed max-w-[320px] mx-auto">Your wallet lives inside your leagues. Once you're in one, you can deposit here.</p>
      <div className="flex gap-3 mt-5 max-w-[360px] mx-auto">
        <button onClick={onCreate} className="flex-1 h-11 rounded-full bg-primary text-white font-display font-bold text-[15px]">Create a league</button>
        <button onClick={onBrowse} className="flex-1 h-11 rounded-full border border-border text-foreground font-semibold text-[15px]">Browse leagues</button>
      </div>
    </div>
  );
}

// Review fix 1: render under the app's real chrome (NFLNav) via WalletShell.
// SAB-572 DEBUG: prints the baked geo flag the moment the deposit chunk loads, so the
// device test shows preview(true)-vs-prod(false) without needing to reach submit. Remove after.
console.log('[SAB-572] WalletDeposit chunk loaded — GEO_ENABLED=', GEO_ENABLED, 'rawVITE_GEO_ENABLED=', import.meta.env.VITE_GEO_ENABLED);

export default function WalletDeposit() {
  return (
    <WalletShell>
      <WalletDepositInner />
    </WalletShell>
  );
}
